import {
  AtSign,
  Clock,
  Computer,
  FolderOpen,
  Laptop,
  Layers,
  Mail,
  PcCase,
  Router,
  Ruler,
  Server,
  Wifi,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "components/ui/badge";
import { Separator } from "components/ui/separator";
import { Item } from "components/ui/item";
import { CoordinatesToGoogle } from "components/CoordinatesToGoogle";
import { DetailItem } from "components/DetailItem";
import { formatDate } from "components/helpers";
import { CASE_TIERS } from "constants/Case";
import { DEFAULT_PAGE_SIZE } from "constants/Constants";

const getCases = (marker = {}) => {
  const cases =
    marker.type === "event"
      ? (marker.data?.machines || []).reduce((acc, machine, index) => {
          const caseData = (marker?.data?.cases || []).find(
            c => c.case_id === marker.data.machines[index].case_id
          );
          if (!caseData) return acc;
          acc[caseData.id] = acc[caseData.id] || { ...caseData };
          acc[caseData.id].machines = acc[caseData.id].machines || {};
          acc[caseData.id].machines[machine.id] = { ...machine };
          return acc;
        }, {})
      : {};

  const distances = [...(marker.data?.distances || [])].sort(
    (a, b) => a.distance - b.distance
  );

  return { cases: Object.values(cases), distances };
};

function EventCount({ count }) {
  if (count <= 1) {
    return null;
  }
  return (
    <div className="tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:font-medium ">
      <Layers className="tw:size-3.5 tw:shrink-0" />
      <span>{count} events at this location</span>
    </div>
  );
}

function MachineDetails({ machine }) {
  const wifiAccessPoints = machine.active_wifi_access_point || [];
  const emailDomains = Object.keys(machine.email_domains || {});
  const eventTypes = Object.keys(machine.event_types || {});
  const hostNames = machine.hostname || [];
  const clientEmails = machine.client_email_addresses || [];

  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5 tw:overflow-y-auto">
      <DetailItem
        label="Last event"
        icon={Clock}
        content={machine.last_event ? formatDate(machine.last_event) : "—"}
      />
      <DetailItem
        label="Active WiFi AP"
        icon={Wifi}
        content={
          wifiAccessPoints.length > 0 ? wifiAccessPoints.join(", ") : "—"
        }
      />
      <DetailItem
        label="Email domains"
        icon={AtSign}
        content={emailDomains.length > 0 ? emailDomains.join(", ") : "—"}
      />

      <DetailItem
        label="Event Types"
        icon={Zap}
        content={eventTypes.length > 0 ? eventTypes.join(", ") : "—"}
      />

      <DetailItem
        label="Host Names"
        icon={PcCase}
        content={hostNames.length > 0 ? hostNames.join(", ") : "—"}
      />

      <DetailItem
        label="Client Emails"
        icon={Mail}
        content={clientEmails.length > 0 ? clientEmails.join(", ") : "—"}
      />
    </div>
  );
}

function EventContent({ cases, distances, position, count, machine }) {
  return (
    <div className="tw:flex tw:max-h-80 tw:min-w-64 tw:flex-col tw:gap-3">
      <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3 tw:overflow-y-auto">
        <EventCount count={count} />
        <Item variant="muted">
          {cases.map(caseData => (
            <div key={caseData.id} className="tw:flex tw:flex-col tw:gap-2">
              <div className="tw:flex tw:items-start tw:gap-1.5">
                <FolderOpen className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-(--color-text-secondary)" />
                <Link
                  target="_blank"
                  to={`/cases/${caseData.id}/machines?page=1&per_page=${DEFAULT_PAGE_SIZE}`}
                  className="tw:font-medium tw:text-(--color-highlight) tw:hover:underline"
                >
                  {caseData.name} — {caseData.case_id}
                </Link>
              </div>

              <div className="tw:flex tw:flex-col tw:gap-1.5 tw:pl-5">
                {Object.values(caseData.machines).map(caseMachine => (
                  <div
                    key={caseMachine.machine_id}
                    className="tw:flex tw:items-center tw:justify-between tw:gap-2"
                  >
                    <div className="tw:flex tw:items-center tw:gap-1.5">
                      <Server className="tw:size-3.5 tw:shrink-0 tw:text-(--color-text-muted)" />
                      <Link
                        target="_blank"
                        to={`/cases/${caseData.id}/events?page=1&per_page=${DEFAULT_PAGE_SIZE}&machine_id=${caseMachine.machine_id}`}
                        className="tw:font-medium tw:text-(--color-highlight) tw:hover:underline"
                      >
                        {caseMachine.machine_id}
                      </Link>
                    </div>
                    <Badge
                      variant={
                        caseMachine.case_tier === CASE_TIERS.UNUSABLE
                          ? "destructive"
                          : "secondary"
                      }
                      size="sm"
                    >
                      {caseMachine.case_tier === CASE_TIERS.UNUSABLE
                        ? "Unusable"
                        : `Tier ${caseMachine.case_tier}`}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Item>

        {machine && <MachineDetails machine={machine} />}

        {distances.length > 0 && (
          <div className="tw:flex tw:flex-col tw:gap-1">
            {distances.map(location => (
              <div
                key={location.address}
                className="tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-(--color-text-secondary)"
              >
                <Ruler className="tw:size-3.5 tw:shrink-0" />
                <span>
                  {location.distance.toFixed(2)} m from {location.address}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <Separator />
      <CoordinatesToGoogle position={position} />
    </div>
  );
}
function LocationDetail({ markerData = {} }) {
  const eventType = markerData.event_type || "—";
  const serverTimeStamp = markerData.server_timestamp;
  const computerDomain = markerData.computer_domain || "—";
  const emailDomains = markerData.email_domains || [];
  const wifiAccessPoints = markerData.active_wifi_access_point || [];
  const machineId = markerData.machine_id || "—";
  const activeMac = markerData.active_mac || "—";
  const hostname = markerData.hostname || "—";
  const clientEmailAddress = markerData.client_email_address || "—";
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5 tw:overflow-y-auto">
      <DetailItem label="Event Type" icon={Zap} content={eventType} />
      <DetailItem
        label="Server Timestamp"
        icon={PcCase}
        content={serverTimeStamp ? formatDate(serverTimeStamp) : "—"}
      />
      <DetailItem
        label="Computer Domain"
        icon={Computer}
        content={computerDomain}
      />
      <DetailItem
        label="Email Domains"
        icon={AtSign}
        content={emailDomains.length > 0 ? emailDomains.join(", ") : "—"}
      />
      <DetailItem
        label="Wifi AP"
        icon={Wifi}
        content={
          wifiAccessPoints.length > 0 ? wifiAccessPoints.join(", ") : "—"
        }
      />
      <DetailItem label="Machine ID" icon={Laptop} content={machineId} />
      <DetailItem label="Active MAC" icon={Router} content={activeMac} />
      <DetailItem label="Hostname" icon={PcCase} content={hostname} />
      <DetailItem
        label="Client Email Address"
        icon={Mail}
        content={clientEmailAddress}
      />
    </div>
  );
}

function SimpleContent({ marker }) {
  const hasTitle =
    marker.title !== `${marker.position.lat}, ${marker.position.lng}`;

  return (
    <div className="tw:flex tw:max-h-80 tw:min-w-64 tw:flex-col tw:gap-3">
      {hasTitle && (
        <div className="tw:font-medium tw:text-(--color-text-primary)">
          {marker.title}
        </div>
      )}
      {marker.type === "location" ? (
        <LocationDetail markerData={marker.data} />
      ) : undefined}
      <Separator />
      <CoordinatesToGoogle position={marker.position} />
    </div>
  );
}

export function AppMapInfo({ marker }) {
  const { cases, distances } = getCases(marker);

  return (
    <div className="tw:p-2">
      {marker.type === "event" ? (
        <EventContent
          cases={cases}
          distances={distances}
          position={marker.position}
          count={marker.count}
          machine={marker.data?.machines?.[0]}
        />
      ) : (
        <SimpleContent marker={marker} />
      )}
    </div>
  );
}
